import { useState } from "react";
import { CATEGORIES, today, todayStr } from "../constants/categories";
import { trackHistoryStyleChanged } from "../services/analytics";
import CalendarStyle from "./history/CalendarStyle";
import DuolingoStyle from "./history/DuolingoStyle";
import GithubStyle from "./history/GithubStyle";
import WarriorStyle from "./history/WarriorStyle";
import TimelineStyle from "./history/TimelineStyle";
import InsightStyle from "./history/InsightStyle";
import JournalStyle from "./history/JournalStyle";
import SelectedDayDetail from "./history/SelectedDayDetail";

const pad = n => String(n).padStart(2, "0");
const toDateStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthNames = ["Tháng 1","Tháng 2","Tháng 3","Tháng 4","Tháng 5","Tháng 6","Tháng 7","Tháng 8","Tháng 9","Tháng 10","Tháng 11","Tháng 12"];

export default function HistoryView({
  history, streak, calMonth, calYear, setCalMonth, setCalYear,
  selectedDay, setSelectedDay, historyStyle, setHistoryStyle,
}) {
  const [filterCat, setFilterCat] = useState(null);

  const changeStyle = (style) => {
    setHistoryStyle(style);
    trackHistoryStyleChanged(style);
  };

  // Filter history by category if selected
  const filteredHistory = filterCat ? history.filter(h => h.category_id === filterCat) : history;

  // Build history map
  const histMap = {};
  filteredHistory.forEach(h => {
    if (!histMap[h.date]) histMap[h.date] = [];
    histMap[h.date].push(h);
  });
  const completedCount = new Set(filteredHistory.filter(h => h.completed).map(h => h.date)).size;
  const totalDays = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  const completionRate = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;

  // Monthly calendar data
  const monthDays = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay();

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); setSelectedDay(null); };
  const nextMonth = () => {
    if (calMonth === today.getMonth() && calYear === today.getFullYear()) return;
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); setSelectedDay(null);
  };
  const isCurrentMonth = calMonth === today.getMonth() && calYear === today.getFullYear();

  // Shared props for all style views
  const sharedProps = {
    histMap, history: filteredHistory, completedCount, streak, completionRate,
    calMonth, calYear, monthDays, firstDayOfWeek, monthNames,
    prevMonth, nextMonth, isCurrentMonth,
    selectedDay, setSelectedDay,
  };

  // Duolingo-specific: this week data
  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
  const thisWeek = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    const dateStr = toDateStr(d);
    const dayItems = histMap[dateStr] || [];
    thisWeek.push({ date: dateStr, dayName: ["T2","T3","T4","T5","T6","T7","CN"][i], items: dayItems, isFuture: dateStr > todayStr, isToday: dateStr === todayStr });
  }
  const weekCompleted = thisWeek.filter(d => d.items.some(c => c.completed)).length;
  const isPerfectWeek = weekCompleted === 7;

  // Streak milestones
  const streakNum = streak.current_streak || 0;
  const nextMilestone = streakNum < 7 ? 7 : streakNum < 14 ? 14 : streakNum < 30 ? 30 : streakNum < 50 ? 50 : streakNum < 100 ? 100 : streakNum < 365 ? 365 : 1000;
  const milestoneProgress = Math.min((streakNum / nextMilestone) * 100, 100);

  // GitHub-specific: year heatmap data
  const heatmapWeeks = [];
  if (historyStyle === "github") {
    const yearStart = new Date(calYear, 0, 1);
    const yearEnd = calYear === today.getFullYear() ? today : new Date(calYear, 11, 31);
    let currentDate = new Date(yearStart);
    currentDate.setDate(currentDate.getDate() - yearStart.getDay());
    while (currentDate <= yearEnd || heatmapWeeks.length < 1) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const dateStr = toDateStr(currentDate);
        const inYear = currentDate >= yearStart && currentDate <= yearEnd;
        week.push({ date: dateStr, inYear, items: histMap[dateStr] || [] });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      heatmapWeeks.push(week);
      if (currentDate > yearEnd) break;
    }
  }

  return (
    <div>
      {/* View Style Toggle */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "20px", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none", paddingBottom: "4px" }}>
        <button onClick={() => changeStyle("duolingo")}
          style={{ background: historyStyle === "duolingo" ? "#FF960022" : "var(--bg-card)", border: `1px solid ${historyStyle === "duolingo" ? "#FF9600" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "duolingo" ? "#FF9600" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          🔥 Streak
        </button>
        <button onClick={() => changeStyle("calendar")}
          style={{ background: historyStyle === "calendar" ? "#EF444422" : "var(--bg-card)", border: `1px solid ${historyStyle === "calendar" ? "#EF4444" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "calendar" ? "#EF4444" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          📅 Calendar
        </button>
        <button onClick={() => changeStyle("github")}
          style={{ background: historyStyle === "github" ? "#10B98122" : "var(--bg-card)", border: `1px solid ${historyStyle === "github" ? "#10B981" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "github" ? "#10B981" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          📊 Heatmap
        </button>
        <button onClick={() => changeStyle("warrior")}
          style={{ background: historyStyle === "warrior" ? "#8B5CF622" : "var(--bg-card)", border: `1px solid ${historyStyle === "warrior" ? "#8B5CF6" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "warrior" ? "#8B5CF6" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          ⚔️ Warrior
        </button>
        <button onClick={() => changeStyle("timeline")}
          style={{ background: historyStyle === "timeline" ? "#14B8A622" : "var(--bg-card)", border: `1px solid ${historyStyle === "timeline" ? "#14B8A6" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "timeline" ? "#14B8A6" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          📜 Timeline
        </button>
        <button onClick={() => changeStyle("insight")}
          style={{ background: historyStyle === "insight" ? "#E11D4822" : "var(--bg-card)", border: `1px solid ${historyStyle === "insight" ? "#E11D48" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "insight" ? "#E11D48" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          📈 Insight
        </button>
        <button onClick={() => changeStyle("journal")}
          style={{ background: historyStyle === "journal" ? "#C0392B22" : "var(--bg-card)", border: `1px solid ${historyStyle === "journal" ? "#C0392B" : "var(--border-primary)"}`, borderRadius: "20px", padding: "6px 16px", color: historyStyle === "journal" ? "#C0392B" : "var(--text-muted)", cursor: "pointer", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
          📖 Journal
        </button>
      </div>

      {/* Category Filter */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
        <button onClick={() => setFilterCat(null)}
          style={{
            background: !filterCat ? "var(--text-muted)" : "transparent",
            border: `1px solid ${!filterCat ? "var(--text-muted)" : "var(--border-secondary)"}`,
            borderRadius: "16px", padding: "4px 12px",
            color: !filterCat ? "#fff" : "var(--text-muted)",
            cursor: "pointer", fontSize: "11px", fontWeight: "600",
          }}>
          Tất cả
        </button>
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setFilterCat(filterCat === cat.id ? null : cat.id)}
            style={{
              background: filterCat === cat.id ? `${cat.color}22` : "transparent",
              border: `1px solid ${filterCat === cat.id ? cat.color : "var(--border-secondary)"}`,
              borderRadius: "16px", padding: "4px 10px",
              color: filterCat === cat.id ? cat.color : "var(--text-muted)",
              cursor: "pointer", fontSize: "11px", fontWeight: "600",
            }}>
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {historyStyle === "calendar" ? (
        <CalendarStyle {...sharedProps} />
      ) : historyStyle === "duolingo" ? (
        <DuolingoStyle {...sharedProps}
          thisWeek={thisWeek} weekCompleted={weekCompleted} isPerfectWeek={isPerfectWeek}
          streakNum={streakNum} nextMilestone={nextMilestone} milestoneProgress={milestoneProgress}
        />
      ) : historyStyle === "warrior" ? (
        <WarriorStyle {...sharedProps} />
      ) : historyStyle === "timeline" ? (
        <TimelineStyle {...sharedProps} />
      ) : historyStyle === "insight" ? (
        <InsightStyle {...sharedProps} />
      ) : historyStyle === "journal" ? (
        <JournalStyle {...sharedProps} />
      ) : (
        <GithubStyle {...sharedProps} heatmapWeeks={heatmapWeeks} />
      )}

      <SelectedDayDetail selectedDay={selectedDay} setSelectedDay={setSelectedDay} />

      {/* Motivation */}
      {completedCount > 0 && (
        <div style={{ textAlign: "center", padding: "20px", fontSize: "13px" }}>
          <div style={{ color: "#FF9600", fontWeight: "700", marginBottom: "4px" }}>
            {streakNum >= 100 ? "🏆 Huyền thoại!" : streakNum >= 30 ? "🔥 Không thể cản!" : streakNum >= 7 ? "💪 Thói quen đang hình thành!" : "🌱 Khởi đầu tốt!"}
          </div>
          <div style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
            {streakNum >= 30 ? `"Bạn đã vượt qua ${streakNum} ngày! Khó khăn nhất là bắt đầu — bạn đã làm được rồi."` :
             streakNum >= 7 ? `"${streakNum} ngày liên tiếp! Cứ tiếp tục, đừng bỏ cuộc."` :
             `"Mỗi bước nhỏ hôm nay là nền tảng cho sự thay đổi lớn ngày mai."`}
          </div>
        </div>
      )}
    </div>
  );
}
